import { type RefObject, useContext, useLayoutEffect, useState } from 'react';
import { useScrollToTopFixIOS } from '@hungryroot/environment';
import {
  Tempeh,
  BeansPlusLentils,
  PlantBased,
  PlantBasedEgg,
  DairyFreeCheese,
} from '@hungryroot/icons';
import { tagGroups } from '@hungryroot/tags';
import { ControlTagTernaryItem } from '../../components/ControlTagTernaryItem/ControlTagTernaryItem';
import { ControlTagTernaryRating } from '../../components/ControlTagTernaryRating/ControlTagTernaryRating';
import { QuizQuestionScreen } from '../../components/QuizQuestionScreen/QuizQuestionScreen';
import { type QuizToastObj } from '../../components/QuizToast/quiz-toast';
import { QuizToast } from '../../components/QuizToast/QuizToast/QuizToast';
import { SectionLabel } from '../../components/SectionLabel/SectionLabel';
import { useFilteredQuizTags } from '../../main';
import { QuizContext } from '../../quiz-context';
import styles from '../layout.module.css';

interface Props {
  testid: string;
  scrollRef: RefObject<HTMLDivElement>;
}

export function Plants(props: Props) {
  const { testid, scrollRef } = props;

  const { quizToast } = useContext(QuizContext);
  const [toastShown, setToastShown] = useState(false);

  useScrollToTopFixIOS(scrollRef);

  useLayoutEffect(() => {
    if (!quizToast || toastShown) {
      return;
    }

    const { clearToasts } = quizToast;
    clearToasts();

    const toastTimeout = setTimeout(() => {
      setToastShown(true);
      renderToast(quizToast);
    }, 2000);

    return () => {
      clearTimeout(toastTimeout);
    };
  }, [quizToast, toastShown]);

  const renderToast = (quizToast: QuizToastObj) => {
    const {
      content: { text, icon },
      toast,
    } = quizToast;

    return toast(
      <QuizToast testid={`${testid}-toast`} icon={icon}>
        {text}
      </QuizToast>
    );
  };

  const { getDisplayTagsFilter } = useFilteredQuizTags();

  return (
    <QuizQuestionScreen
      testid={testid}
      heroText="What kinds of plant-based proteins do you like?"
      sectionLabel={<SectionLabel label="Your tastes" />}
      questionSectionClassName={styles['question-section-protein-page']}
    >
      <ControlTagTernaryRating
        question="plants"
        tagGroupId={tagGroups.plantProtein}
        testid="quiz-page-plants"
        filter={getDisplayTagsFilter('plantBased')}
      >
        {({ tag, ternaryRating, index }) => {
          const { id: tagId, name: tagName, remark: remark } = tag;
          return (
            <ControlTagTernaryItem
              key={tagId}
              tagName={tagName}
              remark={remark}
              ternaryRating={ternaryRating}
              index={index}
              Icon={getIllustration({ tagId })}
            />
          );
        }}
      </ControlTagTernaryRating>
    </QuizQuestionScreen>
  );
}

function getIllustration({ tagId }: { tagId: number }) {
  switch (tagId) {
    case 7:
      return Tempeh;
    case 9:
      return BeansPlusLentils;
    case 10:
      return PlantBased;
    case 12:
      return DairyFreeCheese;
    case 13:
      return PlantBasedEgg;
    default:
      return PlantBased;
  }
}
